// Account — the signed-in Author's own identity, pure.
//
// RECONCILED to the LIVE FLAT backend (IDD 94442b9d, superseding the
// tenant›workspace›project draft this file started from): the `users` table
// is `(id, email, createdAt)` — no display-name column — and workspace
// access is exactly "owner" | "member" on a single-level workspace
// (packages/server/src/auth/members.ts). No tenant grouping, no
// admin/author/reviewer/viewer richness: this shows the one workspace the
// caller is currently in, the caller's role there, and the two account-level
// actions (sign out, delete account). The workspace SWITCHER lives here too
// — it collapses to a plain label when there is nothing to switch between
// (the live single-workspace-per-user default), matching
// `sketches/iam-shell/IamShell.tsx`'s `SingleWorkspaceLocalFirst` story.
//
// Pure/props-driven: no state, no I/O.

export type { AccountProps };
export { Account };

import { Trans } from "@lingui/react/macro";
import { Button } from "../../Button/Button.js";
import { Text } from "../../Text/Text.js";
import {
    type AccountWorkspace,
    WorkspaceSwitcher,
} from "../WorkspaceSwitcher/WorkspaceSwitcher.js";
import "./Account.css";

type AccountProps = {
    readonly email: string;
    readonly workspaces: readonly AccountWorkspace[];
    readonly activeWorkspaceId: string;
    readonly onSwitchWorkspace: (workspaceId: string) => void;
    readonly onSignOut: () => void;
    readonly onRequestDeleteAccount: () => void;
    readonly className?: string;
};

const initialsOf = (email: string): string => email.slice(0, 2).toUpperCase();

const Account = ({
    email,
    workspaces,
    activeWorkspaceId,
    onSwitchWorkspace,
    onSignOut,
    onRequestDeleteAccount,
    className,
}: AccountProps) => {
    return (
        <div className={`bilda-iam-account${className ? ` ${className}` : ""}`}>
            <div className="bilda-iam-account__identity">
                <span className="bilda-iam-account__avatar" aria-hidden="true">
                    {initialsOf(email)}
                </span>
                <Text weight="medium" size="sm">
                    {email}
                </Text>
                <Button
                    variant="ghost"
                    size="sm"
                    className="bilda-iam-account__sign-out"
                    onClick={onSignOut}
                >
                    <Trans>Sign out</Trans>
                </Button>
            </div>

            <div className="bilda-iam-account__section">
                <Text weight="medium" size="sm">
                    <Trans>Workspace</Trans>
                </Text>
                <WorkspaceSwitcher
                    workspaces={workspaces}
                    activeWorkspaceId={activeWorkspaceId}
                    onSwitchWorkspace={onSwitchWorkspace}
                />
            </div>

            <Button
                variant="ghost"
                size="sm"
                className="bilda-iam-account__danger"
                onClick={onRequestDeleteAccount}
            >
                <Trans>Delete account</Trans>
            </Button>
        </div>
    );
};
